import React, {useCallback, useEffect, useState} from 'react';
import {
  ImageStyle,
  TextStyle,
  View,
  ViewStyle,
  StyleSheet,
  Pressable,
} from 'react-native';
import colors from 'theme/Colors';
import {TextInput, Button, Text} from 'ui-kits';
import {
  ErrorCodes,
  getErrorMessage,
  getSuccessMessage,
  SuccessCodes,
} from 'utils/getMessages';
import {corner_radius, spacing} from 'theme/Units';
import {useDispatch, useSelector} from 'react-redux';
import {
  loginRequest,
  resetMessage,
  resetPasswordOtpRequest,
  verifyOTPRequest,
  setCanResetPassword,
  setEmailToRecover,
} from 'redux/slices/authSlice';
import {useToast} from 'hooks';
import {getAuthSuccess, getAuthError} from 'redux/selectors/authSelector';
import {NativeStackScreenProps} from '@react-navigation/native-stack';
import {RootNavigatorParams, Routes} from 'navigation/Routes';
import {LoginValidation} from 'utils/schema';
import {Formik} from 'formik';
import {useResponsiveStyles} from 'hooks/useResponsiveStyles';
import {ForgetPasswordModal} from 'components';
import {GradientWithLogo} from 'components/auth-screen/GradientWithLogo';


type formType = {
  email_id: string;
  password: string;
};

export function SignInScreen({
  navigation,
}: NativeStackScreenProps<RootNavigatorParams>): JSX.Element {
  const dispatch = useDispatch();

  const successSignin = useSelector(getAuthSuccess) as SuccessCodes;
  const errorSignIn = useSelector(getAuthError) as ErrorCodes;

  const {showToasts} = useToast();

  const [isForgotPasswordModalOpen, setIsForgotPasswordModalOpen] =
    useState<boolean>(false);
  const [isOTPSent, setIsOTPSent] = useState<boolean>(false);
  const [recoverEmail, setRecoverEmail] = useState<string>('');

  const gotoResetPasswordScreen = useCallback(() => {
    navigation.navigate(Routes.FORGOT_PASSWORD_PRE_LOGIN)
  }, [navigation])

  useEffect(() => {
    if (successSignin) {
      if (successSignin === 'S-10073') {
        // OTP send successfully
        setIsOTPSent(true);
      }
      // after successfully OTP verification
      if (successSignin === 'S-10074') {
        setIsForgotPasswordModalOpen(false);
        dispatch(setCanResetPassword(true));
        // only after OTP validation dispatch recover email to that to redux
        dispatch(setEmailToRecover(recoverEmail));
        gotoResetPasswordScreen();
      }

      showToasts('success', getSuccessMessage(successSignin), 'success');
      dispatch(resetMessage());
    }
    if (errorSignIn) {
      if (errorSignIn === 'E-10087') {
        setIsForgotPasswordModalOpen(false);
      }
      if (errorSignIn === 'E-10086') {
        setIsForgotPasswordModalOpen(false);
        showToasts('warning', getErrorMessage(errorSignIn), 'warning');
      } else {
        showToasts('error', getErrorMessage(errorSignIn), 'error');
      }
      dispatch(resetMessage());
    }
  }, [showToasts, dispatch, errorSignIn, successSignin, gotoResetPasswordScreen, recoverEmail]);

  const styles = useResponsiveStyles<styleTypes>(baseStyles, responsive);

  const loginFormValues: formType = {
    email_id: '',
    password: '',
  };

  function login(values: formType) {
    dispatch(loginRequest(values));
  }

  function handleForgetPassWord(email: string) {
    dispatch(resetPasswordOtpRequest({email}));
  }

  function handleOTPSubmit(otp: string, email: string) {
    dispatch(verifyOTPRequest({email, otp}));
    // first set the recover email locally
    setRecoverEmail(email);
  }

  return (
    <View style={styles.container}>
      <GradientWithLogo />

      <View style={styles.formContainer}>
        <Formik
          initialValues={loginFormValues}
          validationSchema={LoginValidation}
          onSubmit={login}>
          {({
            handleChange,
            handleBlur,
            handleSubmit,
            values,
            errors,
            dirty,
            isValid,
          }) => {
            return (
              <View style={styles.form}>
                <Text style={styles.heading}>Login</Text>
                <TextInput
                  label={'Email'}
                  size={'md'}
                  error={errors.email_id}
                  onBlur={handleBlur('email_id')}
                  onChangeText={handleChange('email_id')}
                  value={values.email_id}
                  placeholder={'Enter registered email ID'}
                  required
                />
                <TextInput
                  label={'Password'}
                  onBlur={handleBlur('password')}
                  onChangeText={handleChange('password')}
                  size={'md'}
                  value={values.password}
                  error={errors.password}
                  type={'password'}
                  placeholder={'Enter your password'}
                  required
                />
                <Button
                  onPress={handleSubmit}
                  style={styles.submitButton}
                  text="Login"
                  disabled={!isValid || !dirty}
                  textStyle={styles.loginButtonText}
                />
                <Pressable onPress={() => setIsForgotPasswordModalOpen(true)}>
                  <Text
                    variant={'sourcesans14'}
                    style={styles.forgotPasswordText}>
                    Forgot Password?
                  </Text>
                </Pressable>
              </View>
            );
          }}
        </Formik>
        {/* <Text style={styles.singupText}>
          Dont have an account? <Text>Sign Up</Text>
        </Text> */}
      </View>

      <ForgetPasswordModal
        otpSent={isOTPSent}
        onOTPSubmit={handleOTPSubmit}
        open={isForgotPasswordModalOpen}
        onClose={() => setIsForgotPasswordModalOpen(false)}
        onSubmit={handleForgetPassWord}
      />
    </View>
  );
}

type styleTypes = {
  container: ViewStyle;
  formContainer: ViewStyle;
  image: ImageStyle;
  heading: TextStyle;
  submitButton: ViewStyle;
  form: ViewStyle;
  singupText: TextStyle;
  loginButtonText: TextStyle;
  gradient: ViewStyle;
  imageContainer: ViewStyle;
  forgotPasswordText: TextStyle;
};
const baseStyles: styleTypes = StyleSheet.create({
  imageContainer: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  loginButtonText: {color: colors.palette.white},
  gradient: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
  container: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    flex: 1,
  },
  formContainer: {
    backgroundColor: 'white',
    justifyContent: 'center',
    padding: spacing['4'],
  },
  singupText: {
    position: 'absolute',
    bottom: 16,
    left: '50%',
    fontSize: 14,
    transform: 'translate(-50%, 0px)',
    fontWeight: '400',
  },
  form: {
    gap: spacing['4'],
    padding: spacing['4'],
    borderRadius: corner_radius['rounded-lg'],
    alignItems: 'center',
    minWidth: 0,
    width: '100%',
    maxWidth: 400,
    alignSelf: 'center',
    backgroundColor: colors.palette.white,
    boxShadow:
      '0px 2px 4px -2px rgba(23, 24, 28, 0.08), 0px 4px 8px -2px rgba(23, 24, 28, 0.1)',
  },
  submitButton: {width: '100%'},
  heading: {
    color: colors.palette.brandBlue,
    fontSize: 56,
    fontWeight: '600',
  },
  image: {
    width: '60%',
    height: '100%',
  },
  forgotPasswordText: {
    color: colors.palette.blue600,
  },
});

const responsive: Record<number, Partial<styleTypes>> = {
  640: StyleSheet.create({
    container: {
      gridTemplateRows: '1fr 1fr',
      gridTemplateColumns: 'none',
    },
    imageContainer: {
      borderRadius: corner_radius['rounded-lg'],
    },
    form: {
      transform: 'translate(0px, -20%)',
    },
    formContainer: {
      paddingHorizontal: 10,
      justifyContent: 'flex-start',
    },
    heading: {
      fontSize: 44,
    },
  }),
  480: StyleSheet.create({
    heading: {
      fontSize: 32,
    },
  }),
};
